import { useInjection } from 'inversify-react';
import { constant } from 'lodash/fp';
import { transaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext } from 'react';
import { doNothing, doNothingAsync } from '../../../helpers/function.helpers';
import { IUserStore } from '../../user/user.store';
import { IAnnotationService } from '../annotation.service';
import { FREE_DRAW_TOOLS } from '../annotations.interface';
import { IAnnotationsStore } from '../annotations.store';
import { IAnnotationUiStore } from '../annotationUi.store';
import { SegmentationsLayer } from '../components/SegmentationsLayer';
import { IFreeDrawSegmentationService } from '../freeDrawSegmentation.service';
import { IAnnotationTypeBl } from '../submodules/annotationTypes/annotationType.bl';
import { IAnnotationTypeStore } from '../submodules/annotationTypes/annotationType.store';
import { VerticalMenuItem } from '../submodules/segmentations/components/verticalMenu/VerticalMenu';
import { ImageSegmentations } from '../submodules/segmentations/segmentationContextMenu/imageSegmentations.context';

interface IProps {
  readonly?: boolean;
}

export const SegmentationsContainer = observer((props: IProps): ReactElement => {
  const annotationService = useInjection(IAnnotationService.$);
  const annotationUiStore = useInjection(IAnnotationUiStore.$);
  const annotationTypeBl = useInjection(IAnnotationTypeBl.$);
  const annotationTypeStore = useInjection(IAnnotationTypeStore.$);
  const freeDrawSegmentationService = useInjection(IFreeDrawSegmentationService.$);
  const userStore = useInjection(IUserStore.$);
  const annotationsStore = useInjection(IAnnotationsStore.$);
  const imageSegmentationsContext = useContext(ImageSegmentations);

  const handleSegmentationClicked = (id: string): void => {
    if (props.readonly) {
      annotationsStore.drawFocusFrameForId = id;
      annotationService.selectSegmentation(id);
      return;
    }

    transaction(() => {
      annotationService.selectSegmentation(id);
      if (annotationsStore.selectedSegmentation) {
        annotationUiStore.setIsInValidation(false);

        if (FREE_DRAW_TOOLS.includes(annotationsStore.selectedSegmentation.feature.featureType)) {
          freeDrawSegmentationService.setFreeDrawFeature(annotationsStore.selectedSegmentation.feature);
          annotationsStore.drawFocusFrameForId = id;
          const previousAnnotationTypeId = annotationTypeStore.activeAnnotationTypeId;
          const annotationTypeId = annotationsStore.selectedSegmentation.feature.properties?.annotationTypeId;
          if (annotationTypeId !== previousAnnotationTypeId) {
            void annotationTypeBl.handleSegmentationClickedInSegmentationContainer(annotationTypeId).then((succeeded) => {
              if (succeeded && freeDrawSegmentationService.freeDrawFeature) {
                freeDrawSegmentationService.selectFeature();
              }
            });
          }
        } else {
          freeDrawSegmentationService.clear();
          annotationsStore.drawFocusFrameForId = undefined;
        }
      }
    });
  };

  const handleSegmentationTabed = (id: string): void => {
    if (props.readonly) {
      annotationService.selectSegmentation(id);
      return;
    }

    const segmentation = annotationsStore.segmentations.find((s) => s.feature.id === id);

    transaction(() => {
      if (segmentation) {
        if (FREE_DRAW_TOOLS.includes(segmentation.feature.featureType)) {
          freeDrawSegmentationService.setFreeDrawFeature(segmentation.feature);
          freeDrawSegmentationService.selectFeature();
        } else {
          freeDrawSegmentationService.clear();
        }
        annotationsStore.drawFocusFrameForId = segmentation.feature.id;
        void annotationTypeBl.handleSegmentationTapInSegmetationContainer();
      }

      annotationService.selectSegmentation(id);
    });
  };

  const handleSegmentationDeselected = async (): Promise<void> => {
    if (await annotationTypeBl.handleSegmentationDeselectedInSegmentationContainer()) {
      annotationService.deselectSegmentation();
      annotationsStore.drawFocusFrameForId = undefined;
    }
  };

  const handleSegmentationOver = (segmentation: string): void => {
    annotationsStore.hoveredSegmentation = segmentation;
  };

  const handleSegmentationOut = (): void => {
    annotationsStore.hoveredSegmentation = undefined;
  };

  return (
    <>
      {imageSegmentationsContext.activeTab === VerticalMenuItem.ANNOTATED_IN_PROJECTS &&
        imageSegmentationsContext.selectedProjects.map((projectId) => (
          <SegmentationsLayer
            key={projectId}
            imageId={annotationsStore.id ?? ''}
            readonly={true}
            editable={false}
            segmentations={imageSegmentationsContext.segmentations[projectId] || []}
            hiddenSegmentations={annotationsStore.hiddenSegmentations.slice()}
            freeDrawInProgress={false}
            selectedAttributes={userStore.selectedAttributesPerProject.get(annotationsStore.projectId) ?? []}
            showAttributesHeaders={userStore.attributesNamesVisibilityPerProject.get(annotationsStore.projectId) ?? true}
            opacityLevel={userStore.opacityLevel}
            zoomLevel={annotationUiStore.zoomLevel}
            annotationTypesOptions={[]}
            onSegmentationSelected={doNothing}
            onSegmentationDeselected={doNothingAsync}
            onSegmentationTabed={doNothing}
            onSegmentationOver={doNothing}
            onSegmentationOut={doNothing}
            onNextSegmentationSelected={constant(undefined)}
            onPreviousSegmentationSelected={constant(undefined)}
            reactLeafletRenderFingerprint={annotationUiStore.reactLeafletRenderFingerprint}
          />
        ))}

      <SegmentationsLayer
        readonly={props.readonly}
        imageId={annotationsStore.id ?? ''}
        editable={annotationTypeStore.activeAnnotationTypeId === undefined}
        segmentations={annotationsStore.segmentations}
        selectedSegmentation={annotationsStore.selectedSegmentation}
        hoveredSegmentation={annotationsStore.hoveredSegmentation}
        freeDrawInProgress={freeDrawSegmentationService.freeDrawInProgress}
        invalidSegmentation={annotationsStore.invalidSegmentation}
        selectedAttributes={userStore.selectedAttributesPerProject.get(annotationsStore.projectId) ?? []}
        showAttributesHeaders={userStore.attributesNamesVisibilityPerProject.get(annotationsStore.projectId) ?? true}
        onSegmentationSelected={handleSegmentationClicked}
        onSegmentationDeselected={handleSegmentationDeselected}
        onNextSegmentationSelected={() => annotationService.getNextSegmentation()}
        onPreviousSegmentationSelected={() => annotationService.getPreviousSegmentation()}
        onSegmentationTabed={handleSegmentationTabed}
        onSegmentationOver={handleSegmentationOver}
        onSegmentationOut={handleSegmentationOut}
        featureInEditId={annotationsStore.selectedSegmentation?.feature.id}
        opacityLevel={userStore.opacityLevel}
        zoomLevel={annotationUiStore.zoomLevel}
        activeAnnotationTypeId={annotationTypeStore.activeAnnotationTypeId}
        annotationTypesOptions={annotationTypeStore.annotationTypesOptions}
        hiddenSegmentations={annotationsStore.hiddenSegmentations.slice()}
        drawFocusFrameForId={annotationsStore.drawFocusFrameForId}
        reactLeafletRenderFingerprint={annotationUiStore.reactLeafletRenderFingerprint}
      />
    </>
  );
});
